import type { ChangeEvent, FocusEvent, KeyboardEvent, ReactNode } from "react"
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconTriangleWarningOutline18 as AlertTriangle,
	IconAtom2Outline18 as Atom,
	IconOffice3Outline18 as CompanyIcon,
	IconShieldCheckOutline18 as ShieldCheck,
	IconSquaresConnectedOutline18 as SquaresConnected,
	IconUserOutline18 as UserIcon,
} from "nucleo-ui-outline-18"

import type { AgentType } from "@productlane/lib"
import { AGENT_TOKEN, parseSecureRef } from "@productlane/lib"
import { Badge, Button, Tooltip } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { RefsState } from "../model/refs-state"
import type { SecureRefOption } from "../model/secure-ref-picker"
import {
	decodeRef,
	decodeRefText,
	encodeRefText,
	refDisplayMaps,
} from "../model/secure-ref-display"
import {
	activeTokenAt,
	agentPropertyRef,
	agentTypeHead,
	insertAgentProperty,
	insertRef,
	rankRefOptions,
	splitTypedRef,
	tokenFillsSlot,
	usesWidgetData,
} from "../model/secure-ref-picker"
import { useConnectorTitles } from "../model/use-connector-titles"
import { AnchoredSuggestions } from "./anchored-suggestions"
import { ChippedRefField } from "./chipped-ref-field"
import { HighlightedInput } from "./highlighted-input"
import { HighlightedTextarea } from "./highlighted-textarea"
import {
	tokenizeSecureRef,
	tokenizeTemplate,
	tokenizeTokenBody,
	TokenText,
} from "./policy-token-highlight"
import { ReformatJsonButton } from "./reformat-json-button"
import { useSuggestionCombobox } from "./use-suggestion-combobox"

interface Props {
	name: string
	value: string
	onChange: (next: string) => void
	onCommit: (next: string) => void
	options: readonly SecureRefOption[]
	/** Template mode accepts literal text around {{ref}} tokens. */
	mode: "ref" | "template"
	/** The value is a JSON document, so it needs room and vertical scrolling. */
	multiline?: boolean
	/** Types the field's own schema accepts, offered as `agent.<type>` rows. */
	agentTypes?: readonly AgentType[]
	/**
	 * Property a typed row starts on, for an argument that holds properties rather
	 * than one value. Inserted selected, so the next keystroke renames it.
	 */
	agentProperty?: string
	/** Shown instead of the ref-shape error, for a host that validates the text itself. */
	errorMessage?: string | null
	/** One line under the field, for whatever the host has to say about the value. */
	extraHint?: ReactNode
	/**
	 * Takes the `{{token}}` when a ref field picks an agent row. A ref holds one
	 * reference, so the host swaps the rule to a template that holds the token.
	 */
	onPickAgent?: (template: string) => void
	/** Blur only, unlike onCommit, which a suggestion pick also fires. */
	onBlurText?: (next: string) => void
	refsState: RefsState
	onRetryRefs: () => void
}

/** Only ever shown as an error: the shape belongs where a value is refused. */
const INVALID_REF = "Use widget.<key> or http.<connectorId>.<key>."

const WIDGET_ONLY = "Only set when the customer writes from the widget."

/** Where a dropped connector leaves the field, so the list offers the others. */
const HTTP_PREFIX = "http."

const GROUP_LABELS: Record<SecureRefOption["source"], string> = {
	agent: "Agent input",
	widget: "Verified widget data",
	connector: "Connector data",
	contact: "Contact",
	company: "Company",
}

function sourceIcon(source: SecureRefOption["source"]) {
	const className = "h-3.5 w-3.5 flex-none text-tertiary"
	if (source === "agent") return <Atom className={className} />
	if (source === "widget") return <ShieldCheck className={className} />
	if (source === "contact") return <UserIcon className={className} />
	if (source === "company") return <CompanyIcon className={className} />
	return <SquaresConnected className={className} />
}

/** The model's own value for the field, offered only where a template can wrap it. */
const AGENT_OPTION: SecureRefOption = {
	ref: "agent",
	label: "agent",
	source: "agent",
	sample: "the value the agent supplies for this field",
}

function agentHeadOption(head: string): SecureRefOption {
	if (head === AGENT_TOKEN) return AGENT_OPTION
	return {
		ref: head,
		label: head,
		source: "agent",
		sample: "that property of the agent's value",
	}
}

const TYPE_SAMPLE: Record<AgentType, string> = {
	string: "the agent's value as text",
	number: "the agent's value as a number",
	integer: "the agent's value as a whole number",
	boolean: "the agent's value as true or false",
	null: "the agent's value as null",
	json: "the agent's value as an object or a list",
}

// Typed off the head the query names, so `agent.limit.` offers the same types for
// that property as `agent.` does for the whole value.
function agentOptions(
	types: readonly AgentType[],
	head: string,
): SecureRefOption[] {
	return [
		agentHeadOption(head),
		...types.map((type) => ({
			ref: `${head}.${type}`,
			label: `${head}.${type}`,
			source: "agent" as const,
			sample: TYPE_SAMPLE[type],
		})),
	]
}

function sampleNote(option: SecureRefOption): string {
	if (option.remembered) return "seen earlier"
	return option.hidden ? "hidden" : ""
}

export function SecureRefAutocomplete({
	name,
	value,
	onChange,
	onCommit,
	options,
	mode,
	multiline,
	agentTypes,
	agentProperty,
	errorMessage,
	extraHint,
	onPickAgent,
	onBlurText,
	refsState,
	onRetryRefs,
}: Props) {
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
	const pendingSelection = useRef<{ start: number; end: number } | null>(null)
	const refocus = useRef(false)

	const [query, setQuery] = useState("")
	// A ref typed by hand stays whole until blur, so the chip cannot form under
	// the caret halfway through the id.
	const [typingFull, setTypingFull] = useState(false)
	// An emptied key leaves no ref to parse, so the chip it belonged to is held
	// here: clearing the key browses that connector rather than starting over.
	const [heldSection, setHeldSection] = useState<string | null>(null)
	// Template mode only suggests inside a {{token}}; literal text around one is
	// the author's own, so a list over it is noise.
	const [inToken, setInToken] = useState(false)
	// A typed token has to be the whole value, so the types are offered only while
	// the caret sits in a token nothing else surrounds.
	const [tokenIsWhole, setTokenIsWhole] = useState(false)

	const [connectors] = useQuery(queries.sidebarSections())
	const sectionTitles = useConnectorTitles(options)
	const maps = useMemo(() => refDisplayMaps(sectionTitles), [sectionTitles])

	// A template shows connector NAMES in place of ids; what leaves through
	// onChange/onCommit is always the encoded storage form.
	const displayValue = mode === "template" ? decodeRefText(value, maps) : value
	const outbound = (typed: string) =>
		mode === "template" ? encodeRefText(typed, maps) : typed

	useLayoutEffect(() => {
		if (pendingSelection.current === null) return
		const { start, end } = pendingSelection.current
		pendingSelection.current = null
		inputRef.current?.setSelectionRange(start, end)
	}, [value])

	// Template mode filters by the token the caret is in; outside one it lists
	// everything rather than going blank.
	const syncQuery = useCallback(
		(next: string, caret: number) => {
			if (mode === "ref") {
				setQuery(next)
				setInToken(true)
				// The field holds one value, so a token here always owns its slot.
				setTokenIsWhole(true)
				return
			}
			const token = activeTokenAt(next, caret)
			setInToken(!!token)
			setQuery(token?.query ?? "")
			setTokenIsWhole(!!token && tokenFillsSlot(next, token))
		},
		[mode],
	)

	const pick = useCallback(
		(option: SecureRefOption) => {
			// An argument that holds properties is never the whole value a typed token
			// reads, so the token starts out pointed at one of its properties.
			const property = agentProperty
				? agentPropertyRef(option.ref, agentProperty)
				: null

			// A ref holds a reference, not a template, so the host takes the rule swap.
			if (option.source === "agent" && mode === "ref" && onPickAgent) {
				onPickAgent(`{{${property ?? option.ref}}}`)
				return
			}
			const caret = inputRef.current?.selectionStart ?? displayValue.length
			// The text being edited holds names, so what lands in it is a name too.
			const inserted =
				mode === "template" ? decodeRef(option.ref, maps) : option.ref
			const edit = property
				? insertAgentProperty(displayValue, caret, property, mode)
				: insertRef(displayValue, caret, inserted, mode)
			setTypingFull(false)
			setHeldSection(null)
			pendingSelection.current = edit.select ?? {
				start: edit.caret,
				end: edit.caret,
			}
			const stored = outbound(edit.value)
			onChange(stored)
			onCommit(stored)
			syncQuery(edit.value, edit.caret)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			agentProperty,
			mode,
			maps,
			onChange,
			onCommit,
			onPickAgent,
			syncQuery,
			displayValue,
		],
	)

	const trimmed = value.trim()
	const invalid =
		mode === "ref" && trimmed.length > 0 && !parseSecureRef(trimmed)

	// A qualified http ref carries a connector id nobody reads. It moves out of
	// the text into a chip, so the field holds the key alone and the caret still
	// lines up with what is drawn.
	const source = useMemo(() => {
		if (mode !== "ref" || typingFull) return null
		const ref = parseSecureRef(value.trim())
		if (ref?.namespace === "http" && ref.sectionId) {
			return { sectionId: ref.sectionId, key: ref.key }
		}
		return value === "" && heldSection
			? { sectionId: heldSection, key: "" }
			: null
	}, [heldSection, mode, typingFull, value])

	// Once a connector is named, whether by a chip, a half typed id, or a typed
	// display name, the list is that connector's own keys.
	const filtered = useMemo(() => {
		const typed = splitTypedRef(query, maps.toId)
		const sectionId = typed.sectionId ?? source?.sectionId ?? null
		// Only a template can wrap the agent's value; a ref field replaces it whole.
		// A ref field can take one too: picking it swaps the rule to a template.
		const takesAgent = mode === "template" || !!onPickAgent
		const head = agentTypeHead(query)
		const agent =
			takesAgent && !sectionId && head
				? agentOptions(tokenIsWhole ? (agentTypes ?? []) : [], head)
				: []
		const offered = [...agent, ...options]
		const scoped = sectionId
			? offered.filter((option) => option.sectionId === sectionId)
			: offered
		return rankRefOptions(scoped, typed.sectionId ? typed.query : query)
	}, [
		agentTypes,
		maps,
		mode,
		onPickAgent,
		options,
		query,
		source,
		tokenIsWhole,
	])

	const listId = `refs-${name}`
	const combobox = useSuggestionCombobox<
		SecureRefOption,
		HTMLInputElement | HTMLTextAreaElement
	>({
		items: filtered,
		anchorRef: inputRef,
		listId,
		onPick: pick,
		onEnter: () => onCommit(value),
		// The list explains itself when it has nothing to offer, so it opens empty.
		canOpen: mode === "ref" || inToken,
		arrowsOnlyWhenOpen: multiline,
		pickOnTab: true,
		// A ref field holds one reference, so picking one is the whole answer.
		closeOnPick: mode === "ref",
	})

	// The chip and the plain field are two different inputs, so every swap between
	// them unmounts the focused one. Whoever causes a swap flags this to put the
	// caret back, or the list would close under the keystroke that caused it.
	useLayoutEffect(() => {
		if (!refocus.current) return
		refocus.current = false
		inputRef.current?.focus()
		combobox.setFocused(true)
		combobox.setDismissed(false)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [heldSection, source])

	// The catalog takes a round trip to every connector, so the chip's name comes
	// off the synced rows instead: it is right on the first frame.
	const sourceTitle = source
		? (connectors.find((connector) => connector.id === source.sectionId)
				?.title ??
			options.find((option) => option.sectionId === source.sectionId)
				?.connectorTitle ??
			"Connector")
		: null

	// Only HTTP connectors answer a read, so an MCP one in this list would name a
	// source that always refuses.
	const choices = useMemo(
		() =>
			connectors
				.filter((connector) => connector.type === "HTTP")
				.map((connector) => ({ id: connector.id, title: connector.title })),
		[connectors],
	)

	const pickConnector = (sectionId: string) => {
		if (sectionId === source?.sectionId) return
		// An empty key has no ref to carry the id, so the chip holds it instead.
		if (!source?.key) {
			setHeldSection(sectionId)
			return
		}
		const next = `http.${sectionId}.${source.key}`
		onChange(next)
		onCommit(next)
	}

	// A dot means they are writing a whole ref again, so the chip gets out of the
	// way. An emptied key keeps it, and backspace from there removes it.
	const compose = (typed: string) => {
		if (!source) return typed
		if (typed === "") {
			setHeldSection(source.sectionId)
			return ""
		}
		return typed.includes(".") ? typed : `http.${source.sectionId}.${typed}`
	}

	const fieldValue = source ? source.key : displayValue

	const tokens = useMemo(
		() =>
			mode === "ref"
				? tokenizeSecureRef(fieldValue)
				: tokenizeTemplate(fieldValue),
		[fieldValue, mode],
	)

	const widgetOnly = usesWidgetData(value, mode)

	// A widget claim only exists for a widget visitor, so the caveat rides with the
	// value rather than as another line under the field.
	const adornment = widgetOnly ? (
		<Tooltip label={WIDGET_ONLY} side="top" shouldWrapChildren>
			<AlertTriangle
				aria-label={WIDGET_ONLY}
				className="h-3.5 w-3.5 flex-none text-amber-600 dark:text-amber-400"
			/>
		</Tooltip>
	) : null

	// A document has no room at its trailing edge, where the reformat action sits,
	// so the same caveat reads as a line underneath instead.
	const multilineHint =
		widgetOnly || extraHint ? (
			<span className="flex flex-wrap items-center gap-x-1.5">
				{widgetOnly ? (
					<span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
						<AlertTriangle className="h-3.5 w-3.5 flex-none" />
						{WIDGET_ONLY}
					</span>
				) : null}
				{extraHint}
			</span>
		) : undefined

	const fieldProps = {
		...combobox.anchorProps,
		onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const typed = event.target.value
			const caret = event.target.selectionStart ?? typed.length
			combobox.setDismissed(false)
			combobox.setActiveIndex(-1)
			if (!source) setTypingFull(true)
			// A dot hands the whole ref back to the plain field, which swaps the input.
			if (source && typed.includes(".")) {
				refocus.current = true
				pendingSelection.current = { start: caret, end: caret }
			}
			onChange(outbound(compose(typed)))
			syncQuery(typed, caret)
		},
		onFocus: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			combobox.handleFocus(event)
			combobox.setActiveIndex(-1)
			syncQuery(event.target.value, event.target.selectionStart ?? 0)
		},
		onBlur: () => {
			combobox.handleBlur()
			setTypingFull(false)
			setHeldSection(null)
			// A collapsed overlay is shorter than what was scrolled out of view, so
			// it would render off-screen at the old offset.
			if (inputRef.current) inputRef.current.scrollLeft = 0
			onCommit(value)
			onBlurText?.(value)
		},
		onKeyDown: (
			event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
		) => {
			const key = event.key
			// Backspace on an emptied key takes the connector off next, the way it
			// would delete the last token of a pill field.
			if (key === "Backspace" && source && source.key === "") {
				event.preventDefault()
				// Lands on the namespace rather than an empty field, so the next thing
				// picked is another connector. Swapping the chipped field for the plain
				// one takes the caret with it, so it has to be put back.
				refocus.current = true
				setHeldSection(null)
				setTypingFull(true)
				pendingSelection.current = {
					start: HTTP_PREFIX.length,
					end: HTTP_PREFIX.length,
				}
				onChange(HTTP_PREFIX)
				setQuery(HTTP_PREFIX)
				combobox.setActiveIndex(-1)
				return
			}
			// Enter is a newline in a document unless it is picking a suggestion. The
			// value still commits on blur.
			if (key === "Enter" && multiline && !combobox.activeItem) return
			combobox.handleKeyDown(event)
		},
	}

	// Naming a type where none can apply is a dead end otherwise: the bare `agent`
	// row cannot match `agent.<something>`, so the list would just read "no match".
	const namingAgentType = query.startsWith(`${AGENT_TOKEN}.`)

	const empty =
		refsState === "loading" ? (
			<p className="px-3 py-2 text-xs text-tertiary">
				Looking for references your connectors send.
			</p>
		) : refsState === "error" ? (
			<div className="px-3 py-2">
				<p className="text-xs text-tertiary">
					Could not read the references from your connectors.
				</p>
				<Button variant="outline" className="mt-1.5" onClick={onRetryRefs}>
					Try again
				</Button>
			</div>
		) : namingAgentType && !tokenIsWhole ? (
			<p className="px-3 py-2 text-xs text-tertiary">
				A type has to own the whole value, so it cannot sit next to other text.
				Use {"{{agent}}"} here instead.
			</p>
		) : namingAgentType && !agentTypeHead(query) ? (
			<p className="px-3 py-2 text-xs text-tertiary">
				This token already names a type, and a value has one.
			</p>
		) : namingAgentType ? (
			<p className="px-3 py-2 text-xs text-tertiary">
				Add a dot after the property to pick the type it is sent as.
			</p>
		) : (
			<p className="px-3 py-2 text-xs text-tertiary">
				No matching reference. Type one by hand if a connector only sends it for
				some customers.
			</p>
		)

	return (
		<AnchoredSuggestions
			open={combobox.open}
			onOpenChange={(next) => !next && combobox.setDismissed(true)}
			container={combobox.container}
			anchorRef={inputRef}
			listId={listId}
			items={filtered}
			activeIndex={combobox.activeIndex}
			keyOf={(option) => `${option.source}-${option.ref}`}
			groupOf={(option) => GROUP_LABELS[option.source]}
			onHover={combobox.setActiveIndex}
			onPick={combobox.pick}
			keyboardRef={combobox.keyboardRef}
			empty={empty}
			anchor={
				multiline ? (
					<HighlightedTextarea
						ref={inputRef as React.RefObject<HTMLTextAreaElement>}
						name={name}
						id={name}
						value={fieldValue}
						tokens={tokens}
						placeholder={'{ "customer": "{{widget.accountId}}" }'}
						autoIndent
						action={
							<ReformatJsonButton
								// Formatted as storage text: a display name carries quotes
								// JSON cannot hold.
								text={outbound(fieldValue)}
								onFormat={(formatted) => {
									onChange(formatted)
									onCommit(formatted)
								}}
							/>
						}
						hint={multilineHint}
						errorMessage={
							errorMessage ??
							(!combobox.focused && invalid ? INVALID_REF : undefined)
						}
						{...fieldProps}
					/>
				) : source ? (
					<ChippedRefField
						ref={inputRef as React.RefObject<HTMLInputElement>}
						name={name}
						value={source.key}
						sectionId={source.sectionId}
						connectorTitle={sourceTitle ?? "Connector"}
						hint={extraHint}
						choices={choices}
						onPickConnector={pickConnector}
						{...fieldProps}
					/>
				) : (
					<HighlightedInput
						ref={inputRef as React.RefObject<HTMLInputElement>}
						name={name}
						id={name}
						value={fieldValue}
						tokens={tokens}
						placeholder={
							mode === "ref"
								? "widget.stripeCustomerId"
								: "cus_{{widget.accountId}}"
						}
						adornment={adornment}
						hint={extraHint}
						errorMessage={
							errorMessage ??
							(!combobox.focused && invalid ? INVALID_REF : undefined)
						}
						{...fieldProps}
					/>
				)
			}
			renderRow={(option) => (
				<>
					{sourceIcon(option.source)}
					<TokenText
						// The row names the connector inline, the way the field will.
						tokens={tokenizeTokenBody(decodeRef(option.ref, maps))}
						className="flex-none font-mono text-xs"
					/>
					<span className="min-w-0 flex-1 truncate text-xs text-tertiary">
						{option.sample ?? sampleNote(option)}
					</span>
					{option.ambiguous ? <Badge variant="red">Duplicate</Badge> : null}
				</>
			)}
		/>
	)
}
